import { ComponentChildren } from 'preact';
import dayjs from 'dayjs';
import { Pill, PillColor } from '@components/pill';
import { IcoMail, IcoVideoCamera, IcoUsers } from '@components/icons';
import { UserProfileIcon } from '@components/avatars/user-profile-icon';
import type { CommsAgendaItem, CommsAgendaItemType, ScheduleView } from 'shared/comms-schedule';
// Side-effect import: extends dayjs with the timezone, advancedFormat, etc. plugins.
import 'shared/dateutil/date-formats';

type Props = {
  items: CommsAgendaItem[];
  showCourseColumn: boolean;
  view?: ScheduleView;
  emptyState?: ComponentChildren;
};

const typeLabels: Record<CommsAgendaItemType, string> = {
  message: 'Message',
  meetingReminderFirst: 'Reminder · 1st',
  meetingReminderSecond: 'Reminder · 2nd',
};

const statusColors: Record<CommsAgendaItem['status'], PillColor> = {
  scheduled: 'blue',
  sending: 'yellow',
  sent: 'gray',
};

function TypeIcon({ type }: { type: CommsAgendaItemType }) {
  if (type === 'message') {
    return <IcoMail class="w-4 h-4 text-indigo-500 shrink-0" />;
  }
  return <IcoVideoCamera class="w-4 h-4 text-emerald-600 shrink-0" />;
}

// Messages store `start_date` as wall-clock in a tz-less column (the guide
// types "10 AM", recipients receive at 10 AM in their own tz). Meetings store
// `scheduled_at` as a real UTC instant of the moment the call happens. Render
// each accordingly.
function formatSendAt(item: CommsAgendaItem, fmt: string): string {
  return item.type === 'message'
    ? dayjs.utc(item.sendAt).format(fmt)
    : dayjs(item.sendAt).format(fmt);
}

function formatMeetingTime(value: string, fmt: string): string {
  return dayjs(value).format(fmt);
}

function sendAtDayKey(item: CommsAgendaItem): string {
  return item.type === 'message'
    ? dayjs.utc(item.sendAt).format('YYYY-MM-DD')
    : dayjs(item.sendAt).format('YYYY-MM-DD');
}

function groupByDay(
  items: CommsAgendaItem[],
  view: ScheduleView,
): { dayKey: string; label: string; items: CommsAgendaItem[] }[] {
  const map = new Map<string, CommsAgendaItem[]>();
  for (const item of items) {
    const dayKey = sendAtDayKey(item);
    const list = map.get(dayKey) ?? [];
    list.push(item);
    map.set(dayKey, list);
  }
  const today = dayjs().format('YYYY-MM-DD');
  const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');
  const entries = Array.from(map.entries()).sort(([a], [b]) =>
    view === 'sent' ? b.localeCompare(a) : a.localeCompare(b),
  );
  return entries.map(([dayKey, dayItems]) => {
    let label = dayjs(dayKey).format('dddd, MMMM D');
    if (dayKey === today) {
      label = `Today · ${label}`;
    } else if (dayKey === tomorrow) {
      label = `Tomorrow · ${label}`;
    }
    const sortedItems =
      view === 'sent' ? [...dayItems].sort((a, b) => (a.sendAt < b.sendAt ? 1 : -1)) : dayItems;
    return { dayKey, label, items: sortedItems };
  });
}

export function CommsAgenda(props: Props) {
  const { items, showCourseColumn, view = 'upcoming' } = props;

  if (items.length === 0) {
    return (
      <div class="rounded-lg border border-dashed border-gray-300 p-12 text-center text-gray-500">
        {props.emptyState ?? 'No scheduled messages or reminders in this window.'}
      </div>
    );
  }

  const groups = groupByDay(items, view);

  return (
    <div class="flex flex-col gap-8">
      {groups.map((group) => (
        <section key={group.dayKey}>
          <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            {group.label}
          </h3>
          <div class="rounded-lg border border-gray-200 divide-y divide-gray-100 bg-white overflow-visible">
            {group.items.map((item) => (
              <AgendaRow key={item.id} item={item} showCourseColumn={showCourseColumn} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function AgendaRow({
  item,
  showCourseColumn,
}: {
  item: CommsAgendaItem;
  showCourseColumn: boolean;
}) {
  const dimmed = item.status === 'sent';
  // Dim must apply to the inner grid only — wrapping the absolutely-positioned
  // hover preview in an opacity:0.6 ancestor makes the preview translucent
  // (CSS opacity is multiplicative and not undoable downstream), so the rows
  // behind bleed through. Keep the preview as a sibling of the dimmed grid.
  return (
    <a href={item.editUrl} class="group/row relative block hover:bg-gray-50">
      <div
        class={`grid grid-cols-[7rem_1fr_auto] md:grid-cols-[8rem_10rem_1fr_auto] gap-4 items-center px-4 py-3 ${
          dimmed ? 'opacity-60' : ''
        }`}
      >
        <div class="flex flex-col">
          <span class="text-sm font-medium text-gray-900 tabular-nums">
            {formatSendAt(item, 'h:mm A')}
          </span>
        </div>

        <div class="hidden md:flex items-center gap-2 min-w-0">
          <TypeIcon type={item.type} />
          <span class="text-xs text-gray-600 truncate">{typeLabels[item.type]}</span>
        </div>

        <div class="flex flex-col min-w-0">
          <div class="flex items-center gap-2 min-w-0">
            <span class="md:hidden">
              <TypeIcon type={item.type} />
            </span>
            <span class="text-sm font-medium text-gray-900 truncate">{item.subject}</span>
          </div>
          <div class="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
            {showCourseColumn && (
              <span class="truncate max-w-[20rem]" title={item.courseTitle}>
                {item.courseTitle}
              </span>
            )}
            {item.guideName && (
              <span class="inline-flex items-center gap-1.5 min-w-0">
                <UserProfileIcon
                  user={{ name: item.guideName, profilePhotoUrl: item.guidePhotoUrl }}
                  size="size-5"
                  maxWidth={48}
                />
                <span class="truncate">{item.guideName}</span>
              </span>
            )}
            <RecipientCount item={item} />
            {item.type !== 'message' && item.meetingScheduledAt && (
              <span class="hidden sm:inline">
                for {formatMeetingTime(item.meetingScheduledAt, 'MMM D · h:mm A')}
              </span>
            )}
          </div>
        </div>

        <div class="flex items-center justify-end shrink-0">
          {item.status === 'sent' && item.receiptCount && item.receiptCount > 0 ? (
            <EngagementMetric item={item} />
          ) : (
            <Pill color={statusColors[item.status]}>{item.status}</Pill>
          )}
        </div>
      </div>

      <CommsPreviewCard item={item} />
    </a>
  );
}

// Once the email has actually sent (receipts written), the truthful denominator
// is `receiptCount` — that's who Mailgun got. `recipientCount` reflects the
// current enrolled count, which can drift as people join after the send (Lisa
// Bloom's AI Storytelling: 104 sent, 157 enrolled now). Showing the current
// count made the engagement % look mismatched. Tooltip preserves both numbers.
function RecipientCount({ item }: { item: CommsAgendaItem }) {
  const isSent = item.status === 'sent' && !!item.receiptCount && item.receiptCount > 0;
  const display = isSent ? (item.receiptCount as number) : item.recipientCount;
  const tooltip = isSent
    ? `${item.receiptCount} sent · ${item.recipientCount} currently enrolled`
    : undefined;
  return (
    <span class="inline-flex items-center gap-1" title={tooltip}>
      <IcoUsers class="w-3 h-3" />
      {display.toLocaleString()}
    </span>
  );
}

function EngagementMetric({ item }: { item: CommsAgendaItem }) {
  const denom = item.receiptCount ?? 0;
  const opens = item.openedCount ?? 0;
  const clicks = item.clickedCount ?? 0;
  const hasTracking = opens > 0 || clicks > 0;

  if (denom === 0) {
    return <Pill color="gray">sent</Pill>;
  }

  if (!hasTracking) {
    // Sent before we started tracking opens/clicks (or before Mailgun's
    // event retention window). Keep the status pill so the row reads as
    // "sent" rather than implying 0% engagement.
    return <Pill color="gray">sent</Pill>;
  }

  const openPct = Math.round((opens / denom) * 100);
  const clickPct = Math.round((clicks / denom) * 100);
  const tooltip = `${opens} of ${denom} opened (${openPct}%) · ${clicks} of ${denom} clicked (${clickPct}%)`;

  return (
    <span class="text-sm text-gray-700 tabular-nums whitespace-nowrap" title={tooltip}>
      <span class="font-medium">{openPct}%</span>
      <span class="text-gray-400"> · </span>
      <span class="font-medium">{clickPct}%</span>
    </span>
  );
}

function CommsPreviewCard({ item }: { item: CommsAgendaItem }) {
  const hasPreview = !!item.bodyExcerpt;
  if (!hasPreview && !item.guideName && !item.meetingScheduledAt) {
    return null;
  }
  return (
    <div class="hidden md:block absolute left-0 right-0 top-full mt-2 z-30 invisible opacity-0 group-hover/row:visible group-hover/row:opacity-100 group-hover/row:delay-300 transition-opacity duration-150">
      <div class="rounded-lg border border-gray-200 bg-white shadow-xl p-4 max-w-3xl">
        <div class="flex items-baseline justify-between gap-4 mb-2">
          <div class="flex items-center gap-2 min-w-0">
            <TypeIcon type={item.type} />
            <span class="text-base font-semibold text-gray-900 truncate">{item.subject}</span>
          </div>
          <span class="text-xs text-gray-500 shrink-0 tabular-nums">
            {formatSendAt(item, 'ddd, MMM D · h:mm A')}
          </span>
        </div>

        <div class="text-xs text-gray-600 mb-3 flex items-center gap-3 flex-wrap">
          <span>{item.courseTitle}</span>
          {item.guideName && <span>· {item.guideName}</span>}
          {item.status === 'sent' && !!item.receiptCount && item.receiptCount > 0 ? (
            <span>
              · {item.receiptCount.toLocaleString()} sent
              {item.recipientCount !== item.receiptCount &&
                ` (${item.recipientCount.toLocaleString()} enrolled now)`}
            </span>
          ) : (
            <span>
              · {item.recipientCount.toLocaleString()} recipient
              {item.recipientCount === 1 ? '' : 's'}
            </span>
          )}
          {item.status === 'sent' && item.receiptCount && item.receiptCount > 0 && (
            <span>
              · {item.openedCount ?? 0} of {item.receiptCount} opened (
              {Math.round(((item.openedCount ?? 0) / item.receiptCount) * 100)}%) ·{' '}
              {item.clickedCount ?? 0} of {item.receiptCount} clicked (
              {Math.round(((item.clickedCount ?? 0) / item.receiptCount) * 100)}%)
            </span>
          )}
          {item.type !== 'message' && item.meetingScheduledAt && (
            <span>
              · for meeting at {formatMeetingTime(item.meetingScheduledAt, 'MMM D · h:mm A')}
            </span>
          )}
        </div>

        {item.bodyExcerpt && (
          <p class="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
            {item.bodyExcerpt}
          </p>
        )}
      </div>
    </div>
  );
}
